import * as React from 'react'
import { FormattedDate, FormattedRelative } from 'react-intl'
import styled from 'styled-components'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import Table from 'web-makeup/dist/Table'
import { capitalize, get } from 'lodash'

import {
  GetAllProducers,
  GetAllRequestTickets,
  RequestTicketRequesterType,
} from '../../typings/types'
import RequestStateTag from '../RequestDetailsPage/RequestStateTag' // If more than 2 components use this, will move it outside to component folder

interface StatusProps {
  online: boolean
}

export const Status = styled.div`
  width: 6px;
  height: 6px;
  background-color: ${(props: StatusProps) => (props.online ? '#7ed321' : '#f5222d')};
  border-radius: 100%;
`

const Row = styled.div`
  display: grid;
  padding-right: 0.75rem;
  align-items: center;
  grid-template-columns: ${(props: { numberOfColumns: number }) =>
    `repeat(${props.numberOfColumns}, auto)`};
  grid-column-gap: 0.5rem;
  justify-content: space-between;
`

export const Text = styled.span`
  word-break: keep-all;
`

export const StyledTable = styled(Table as any)`
  &&&& {
    .ant-table-small {
      border: none;
    }

    .ant-table-thead {
      tr > th {
        background: #fafafa;
      }
      span {
        word-break: keep-all;
      }
    }
    .ant-table-row {
      cursor: pointer;
    }
  }
`

export enum columnType {
  STATUS = 'STATUS',
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
  DATE = 'DATE',
  DATE_AGO = 'DATE_AGO',
  ICON = 'ICON',
  STATE = 'STATE',
}

export interface ColumnsProps {
  name?: string
  columns: Columns[]
  visible: boolean
  nullResult?: string
}

export interface Columns {
  columnName: string
  columnType: columnType
}

export const renderColumnType = (
  type: columnType,
  data: any,
  nullResult: string | undefined,
  key: number
) => {
  switch (type) {
    case columnType.STATUS:
      return <Status online={data} title={data ? 'Online' : 'Offline'} key={key} />
    case columnType.IMAGE:
      const avatar = {
        id: data ? data.cloudinaryId : 'placeholders/icon-user',
        type: data ? data.cloudinaryType : 'upload',
      }
      return (
        <CloudinaryImage
          source={avatar}
          sizes={{
            mobile: {
              width: 30,
              height: 30,
            },
          }}
          key={key}
        />
      )
    case columnType.DATE:
      return data ? (
        <Text key={key}>
          <FormattedDate value={data} year="numeric" month="long" day="2-digit" />
        </Text>
      ) : (
        <Text key={key}>{nullResult}</Text>
      )
    case columnType.DATE_AGO:
      return data ? (
        <Text key={key}>
          <FormattedRelative value={data} />
        </Text>
      ) : (
        <Text key={key}>{nullResult}</Text>
      )
    case columnType.ICON:
      return data === RequestTicketRequesterType.GEN1_BOT ? '🤖' : '👨'
    case columnType.STATE:
      return <RequestStateTag key={key} state={data} size="small" />
    case columnType.TEXT:
    default:
      return (
        <Text key={key}>
          {data
            .split('_')
            .map(capitalize)
            .join(' ')}
        </Text>
      )
  }
}

interface OwnProps {
  data: GetAllProducers.Producers[] | GetAllRequestTickets.RequestTickets[]
  columns: ColumnsProps[]
  loading: boolean
  onPageChange?: (current: number, pageSize: number) => void
  onRowClick?: (row: any) => void
  pagination?: {
    pageSize?: number
    total?: number
    current?: number
  }
}

const TableList: React.SFC<OwnProps> = props => (
  <StyledTable
    size="small"
    loading={props.loading}
    dataSource={props.data}
    rowKey={(record: GetAllProducers.Producers | GetAllRequestTickets.RequestTickets) => {
      switch (record.__typename) {
        case 'ProducerProfile':
          return record.user.uuid

        case 'RequestTicket':
          return record.uuid

        default:
          return undefined
      }
    }}
    columns={props.columns.map(
      column =>
        column.visible
          ? {
              title: column.name,
              dataIndex: column.columns[0].columnName,
              key: column.columns[0].columnName,
              render: (data, row) =>
                data && (
                  <Row numberOfColumns={column.columns.length}>
                    {column.columns.map((c, i) =>
                      renderColumnType(c.columnType, get(row, c.columnName), column.nullResult, i)
                    )}
                  </Row>
                ),
            }
          : {}
    )}
    onChange={pagination => {
      if (props.onPageChange) {
        props.onPageChange(pagination.current, pagination.pageSize)
      }
    }}
    pagination={props.pagination}
    onRow={row => ({
      onClick: () => {
        if (props.onRowClick) {
          props.onRowClick(row)
        }
      },
    })}
  />
)

export default TableList



// WEBPACK FOOTER //
// ./src/components/TableList/TableList.tsx